import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { epochLabel } from '@/features/projects/format'
import { useAppDispatch, useAppSelector } from '@/store'
import { DIFF_STATUS_TONE } from './tones'
import {
  confirmGuardrails,
  loadActivityTrail,
  loadAuditTrail,
  loadReconciliationTrace,
  loadTrailSummary,
  selectMigrationMutating,
  selectMigrationNonce,
  selectMigrationTrail,
  toDisplayError
} from '../slices/migrationSlice'

const AUDIT_PAGE_SIZE = 25

/** One of the four reads — summary — each with its own loading/error/empty. */
function TrailSummarySection({
  orgId,
  projectId,
  planId,
  nonce
}: {
  orgId: string
  projectId: string
  planId: string
  nonce: number
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const trail = useAppSelector(selectMigrationTrail)
  const mutating = useAppSelector(selectMigrationMutating)
  const [note, setNote] = useState('')
  const summary = trail.summary

  useEffect(() => {
    void dispatch(loadTrailSummary({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId, nonce])

  const onRetryClicked = useCallback(() => {
    void dispatch(loadTrailSummary({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId])

  const onConfirmSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      void dispatch(
        confirmGuardrails({ orgId, projectId, planId, input: note.trim() === '' ? {} : { confirmationNote: note } })
      ).then((result) => {
        if (confirmGuardrails.fulfilled.match(result)) setNote('')
      })
    },
    [dispatch, orgId, projectId, planId, note]
  )

  return (
    <div className="border-b border-border px-4 py-3" data-testid="migration-trail-summary">
      <SectionLabel>{t('migration.trail.summaryTitle')}</SectionLabel>
      {summary.error !== null ? (
        <div className="mt-2">
          <ErrorPanel error={toDisplayError(summary.error)} onRetryClicked={onRetryClicked} />
        </div>
      ) : summary.loading && summary.data === null ? (
        <div className="mt-2">
          <LoadingPanel label={t('migration.trail.loading')} />
        </div>
      ) : summary.data === null ? (
        <div className="mt-2">
          <EmptyPanel title={t('migration.trail.summaryEmptyTitle')} body={t('migration.trail.summaryEmptyBody')} />
        </div>
      ) : (
        <div className="mt-2 space-y-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone="neutral">{t(`migration.planStatus.${summary.data.planStatus}`)}</StatusPill>
            <StatusPill tone={summary.data.latestGoNoGoVerdict === null ? 'neutral' : 'proposed'}>
              {summary.data.latestGoNoGoVerdict === null
                ? t('migration.trail.noVerdictYet')
                : t(`migration.verdict.${summary.data.latestGoNoGoVerdict}`)}
            </StatusPill>
            {summary.data.goLiveInvalidated && (
              <StatusPill tone="blocked">{t('migration.rollback.goLiveInvalidated')}</StatusPill>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('migration.trail.counts', {
              audit: summary.data.auditRowCount,
              activity: summary.data.activityRowCount,
              open: summary.data.openFlaggedDifferenceCount,
              proposed: summary.data.proposedNotYetDisposedCount
            })}
          </p>
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
            <p className="font-medium text-foreground">{t('migration.trail.guardrailsTitle')}</p>
            <p className="mt-1 text-muted-foreground">
              {t('migration.trail.allowlistEnabled', {
                value: summary.data.guardrailStatus.connectorAllowlistEnabled ? t('migration.trail.yes') : t('migration.trail.no')
              })}
            </p>
            <p className="text-muted-foreground">
              {t('migration.trail.gateBound', {
                value: summary.data.guardrailStatus.connectorAllowlistGateBound ? t('migration.trail.yes') : t('migration.trail.no')
              })}
            </p>
            <p className="text-muted-foreground">{summary.data.guardrailStatus.evidence}</p>
            <form onSubmit={onConfirmSubmit} className="mt-2 flex flex-wrap items-end gap-2">
              <Input
                aria-label={t('migration.trail.confirmNoteLabel')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('migration.trail.confirmNotePlaceholder')}
                className="min-w-[14rem] flex-1"
              />
              <Button type="submit" size="sm" disabled={mutating === 'guardrails-confirm'}>
                {t('migration.trail.confirmAction')}
              </Button>
            </form>
            {trail.guardrailError !== null && (
              <p role="alert" className="mt-1 text-blocked">
                {trail.guardrailError.message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function TrailAuditSection({
  orgId,
  projectId,
  planId,
  nonce
}: {
  orgId: string
  projectId: string
  planId: string
  nonce: number
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const trail = useAppSelector(selectMigrationTrail)
  const audit = trail.audit

  useEffect(() => {
    void dispatch(loadAuditTrail({ orgId, projectId, planId, limit: AUDIT_PAGE_SIZE }))
  }, [dispatch, orgId, projectId, planId, nonce])

  const onRetryClicked = useCallback(() => {
    void dispatch(loadAuditTrail({ orgId, projectId, planId, limit: AUDIT_PAGE_SIZE }))
  }, [dispatch, orgId, projectId, planId])

  const onLoadMoreClicked = useCallback(() => {
    const cursor = audit.data?.nextCursor
    if (cursor === null || cursor === undefined) return
    void dispatch(loadAuditTrail({ orgId, projectId, planId, limit: AUDIT_PAGE_SIZE, cursor }))
  }, [dispatch, orgId, projectId, planId, audit.data])

  return (
    <div className="border-b border-border px-4 py-3" data-testid="migration-trail-audit">
      <SectionLabel>{t('migration.trail.auditTitle')}</SectionLabel>
      {audit.error !== null ? (
        <div className="mt-2">
          <ErrorPanel error={toDisplayError(audit.error)} onRetryClicked={onRetryClicked} />
        </div>
      ) : audit.loading && audit.data === null ? (
        <div className="mt-2">
          <LoadingPanel label={t('migration.trail.loading')} />
        </div>
      ) : (audit.data?.items.length ?? 0) === 0 ? (
        <div className="mt-2">
          <EmptyPanel title={t('migration.trail.auditEmptyTitle')} body={t('migration.trail.auditEmptyBody')} />
        </div>
      ) : (
        <>
          <ul className="mt-2">
            {audit.data!.items.map((row) => (
              <li key={row.id} className="v-row flex flex-wrap items-center gap-x-2 py-1.5 text-xs">
                <StatusPill tone="neutral">{row.action}</StatusPill>
                <span className="text-muted-foreground">{epochLabel(row.occurredAt)}</span>
              </li>
            ))}
          </ul>
          {audit.data?.nextCursor !== null && (
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={onLoadMoreClicked}>
              {t('migration.trail.loadMore')}
            </Button>
          )}
        </>
      )}
    </div>
  )
}

function TrailActivitySection({
  orgId,
  projectId,
  planId,
  nonce
}: {
  orgId: string
  projectId: string
  planId: string
  nonce: number
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const trail = useAppSelector(selectMigrationTrail)
  const activity = trail.activity

  useEffect(() => {
    void dispatch(loadActivityTrail({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId, nonce])

  const onRetryClicked = useCallback(() => {
    void dispatch(loadActivityTrail({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId])

  return (
    <div className="border-b border-border px-4 py-3" data-testid="migration-trail-activity">
      <SectionLabel>{t('migration.trail.activityTitle')}</SectionLabel>
      {activity.error !== null ? (
        <div className="mt-2">
          <ErrorPanel error={toDisplayError(activity.error)} onRetryClicked={onRetryClicked} />
        </div>
      ) : activity.loading && activity.data === null ? (
        <div className="mt-2">
          <LoadingPanel label={t('migration.trail.loading')} />
        </div>
      ) : (activity.data?.length ?? 0) === 0 ? (
        <div className="mt-2">
          <EmptyPanel title={t('migration.trail.activityEmptyTitle')} body={t('migration.trail.activityEmptyBody')} />
        </div>
      ) : (
        <ul className="mt-2">
          {activity.data!.map((row) => (
            <li key={row.id} className="v-row flex flex-wrap items-center gap-x-2 py-1.5 text-xs">
              <span className="text-foreground">{row.content ?? row.type ?? '—'}</span>
              {row.createdAtMillis !== undefined && (
                <span className="text-muted-foreground">{epochLabel(row.createdAtMillis)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function TrailTraceSection({
  orgId,
  projectId,
  planId,
  nonce
}: {
  orgId: string
  projectId: string
  planId: string
  nonce: number
}) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const trail = useAppSelector(selectMigrationTrail)
  const trace = trail.trace

  useEffect(() => {
    void dispatch(loadReconciliationTrace({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId, nonce])

  const onRetryClicked = useCallback(() => {
    void dispatch(loadReconciliationTrace({ orgId, projectId, planId }))
  }, [dispatch, orgId, projectId, planId])

  return (
    <div className="px-4 py-3" data-testid="migration-trail-trace">
      <SectionLabel>{t('migration.trail.traceTitle')}</SectionLabel>
      {trace.error !== null ? (
        <div className="mt-2">
          <ErrorPanel error={toDisplayError(trace.error)} onRetryClicked={onRetryClicked} />
        </div>
      ) : trace.loading && trace.data === null ? (
        <div className="mt-2">
          <LoadingPanel label={t('migration.trail.loading')} />
        </div>
      ) : (trace.data?.length ?? 0) === 0 ? (
        <div className="mt-2">
          <EmptyPanel title={t('migration.trail.traceEmptyTitle')} body={t('migration.trail.traceEmptyBody')} />
        </div>
      ) : (
        <ul className="mt-2">
          {trace.data!.map((entry) => (
            <li key={entry.diffId} className="v-row flex flex-wrap items-center gap-x-2 py-1.5 text-xs">
              <span className="font-mono text-foreground">{entry.objectKey}</span>
              <StatusPill tone={DIFF_STATUS_TONE[entry.status]}>{t(`migration.diffStatus.${entry.status}`)}</StatusPill>
              {entry.proposedByUserName !== undefined && (
                <span className="text-muted-foreground">
                  {t('migration.trail.proposedBy', { name: entry.proposedByUserName })}
                </span>
              )}
              {entry.disposedByUserName !== undefined && (
                <span className="text-muted-foreground">
                  {t('migration.trail.disposedBy', { name: entry.disposedByUserName })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * The trail panel (AC-7) — four sub-panels, each with its own
 * loading/error/empty (summary · audit · activity · trace), plus the
 * guardrails-confirm act. Self-contained around the reuse-surround family.
 */
export function MigrationTrailPanel({
  orgId,
  projectId,
  planId
}: {
  orgId: string
  projectId: string
  planId: string | null
}) {
  const { t } = useTranslation('projectWorkspace')
  const nonce = useAppSelector(selectMigrationNonce)

  if (planId === null) {
    return (
      <section aria-label={t('migration.trail.title')} className="rounded-lg border border-border bg-card px-4 py-3.5">
        <SectionLabel>{t('migration.trail.title')}</SectionLabel>
        <div className="mt-2">
          <EmptyPanel title={t('migration.trail.noPlanTitle')} body={t('migration.trail.noPlanBody')} />
        </div>
      </section>
    )
  }

  return (
    <section
      aria-label={t('migration.trail.title')}
      className="overflow-hidden rounded-lg border border-border bg-card"
      data-testid="migration-trail-panel"
    >
      <div className="border-b border-border px-4 py-3">
        <SectionLabel>{t('migration.trail.title')}</SectionLabel>
      </div>
      <TrailSummarySection orgId={orgId} projectId={projectId} planId={planId} nonce={nonce} />
      <TrailAuditSection orgId={orgId} projectId={projectId} planId={planId} nonce={nonce} />
      <TrailActivitySection orgId={orgId} projectId={projectId} planId={planId} nonce={nonce} />
      <TrailTraceSection orgId={orgId} projectId={projectId} planId={planId} nonce={nonce} />
    </section>
  )
}
